import { useCallback, useEffect, useState } from 'react';

import { Auth0ProviderConfig } from '../config/types';
import {
  type AccountLinkStep,
  type ConnectedAccount,
  type LinkAccountOptions,
  MissingConnectCodeError,
  OAuthStateMismatchError,
  clearAuth0AccountLinkCache,
  clearStoredConnected,
  deleteConnectedAccount,
  detachPopupOpener,
  ensureAuth,
  ensureLogoutHandlerRegistered,
  fetchAccountLinks,
  getAuthClient,
  getFailureResult,
  getMyAccountAuthParams,
  initiateAccountLinkWithRetry,
  invalidateAccountsCache,
  myAccountUrl,
  pollForOAuthCallback,
} from './accountLinkHelpers';

export {
  clearAuth0AccountLinkCache,
  getAuth0AccountLinkErrorMessage,
  isPopupBlockedError,
  isTokenVaultPropagationError,
  type AccountLinkStep,
  type UseAuth0AccountLinkResult,
} from './accountLinkHelpers';

export const useAuth0AccountLink = (config: Auth0ProviderConfig) => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectedAccount, setConnectedAccount] = useState<
    ConnectedAccount | undefined
  >();

  useEffect(() => {
    ensureLogoutHandlerRegistered();
    clearStoredConnected(config);
    setIsConnected(false);
    setConnectedAccount(undefined);
  }, [config]);

  const checkConnection = useCallback(async (): Promise<boolean> => {
    try {
      const client = getAuthClient(config);
      const token = await client.getTokenSilently({
        authorizationParams: getMyAccountAuthParams(config),
        timeoutInSeconds: 10,
      });
      const accounts = await fetchAccountLinks(config, token);
      const match = accounts.find(a => a.connection === config.connection);
      const connected = !!match;
      setIsConnected(connected);
      setConnectedAccount(match);
      return connected;
    } catch {
      clearStoredConnected(config);
      setIsConnected(false);
      setConnectedAccount(undefined);
      return false;
    }
  }, [config]);

  const linkAccount = useCallback(
    async ({
      forceRelink = false,
      popup,
      onStep,
    }: LinkAccountOptions): Promise<{
      linked: boolean;
      failureReason?: string;
      failureDebugMessage?: string;
    }> => {
      let substep: AccountLinkStep = 'auth0_token';
      try {
        const client = getAuthClient(config);

        onStep?.('auth0_token');
        let token = await ensureAuth(client, {
          authorizationParams: getMyAccountAuthParams(config),
          popup,
        });

        substep = 'fetch_accounts';
        onStep?.('fetch_accounts');
        const accounts = await fetchAccountLinks(config, token);
        const existingLink = accounts.find(
          a => a.connection === config.connection,
        );

        if (existingLink && forceRelink) {
          substep = 'delete_existing_link';
          onStep?.('delete_existing_link');
          await deleteConnectedAccount(config, token, existingLink.id);
        } else if (existingLink) {
          if (popup && !popup.closed) popup.close();
          setIsConnected(true);
          setConnectedAccount(existingLink);
          return { linked: true };
        }

        substep = 'initiate_link';
        onStep?.('initiate_link');
        const initiateResult = await initiateAccountLinkWithRetry(
          config,
          client,
          token,
        );
        token = initiateResult.token;

        substep = 'provider_popup';
        onStep?.('provider_popup');
        await detachPopupOpener(popup);
        popup.location.href = initiateResult.connectUri;
        const params = await pollForOAuthCallback(
          popup,
          window.location.origin,
        );
        const oauthError = params.get('error');
        if (oauthError) {
          throw new Error(params.get('error_description') ?? oauthError);
        }
        const connectCode = params.get('connect_code');
        const returnedState = params.get('state');

        if (!returnedState || returnedState !== initiateResult.oauthState) {
          throw new OAuthStateMismatchError();
        }

        if (!connectCode) {
          throw new MissingConnectCodeError();
        }

        substep = 'complete_link';
        onStep?.('complete_link');
        const response = await fetch(myAccountUrl(config, 'complete'), {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            auth_session: initiateResult.authSession,
            connect_code: connectCode,
            redirect_uri: window.location.origin,
          }),
        });

        if (!response.ok) {
          throw new Error(
            `Failed to complete account link: ${response.status}`,
          );
        }

        invalidateAccountsCache(config);
        setIsConnected(true);
        return { linked: true };
      } catch (err) {
        return getFailureResult(substep, err);
      }
    },
    [config],
  );

  const unlinkAccount = useCallback(async (): Promise<void> => {
    const client = getAuthClient(config);
    const token = await ensureAuth(client, {
      authorizationParams: {
        audience: config.myAccountAudience,
        scope:
          'openid profile email read:me:connected_accounts delete:me:connected_accounts',
      },
    });

    const accounts = await fetchAccountLinks(config, token);
    const match = accounts.find(a => a.connection === config.connection);
    if (match) {
      await deleteConnectedAccount(config, token, match.id);
    }

    clearStoredConnected(config);
    setIsConnected(false);
    setConnectedAccount(undefined);
  }, [config]);

  const clearAuthCache = useCallback(async (): Promise<void> => {
    await clearAuth0AccountLinkCache();
    setIsConnected(false);
    setConnectedAccount(undefined);
  }, []);

  return {
    linkAccount,
    unlinkAccount,
    isConnected,
    connectedAccount,
    checkConnection,
    clearAuthCache,
  };
};
